/** Area-matrix payloads flattened into floor-area rows. */
import { EstimateUseGroup } from "../../interfaces/EstimateUseGroup";
import { AreaMatrixItem, AreaMatrixItemEstimateUseGroup, FloorGroup, FloorAreaItem } from "../../interfaces/AreaSheet";

export const useGroupAreaLabel = (useGroup: EstimateUseGroup): string =>
  useGroup.label?.trim() || useGroup.use_group_name || useGroup.use_group;

export const transformFloorAreas = (
  area_matrix_items: AreaMatrixItem[],
  area_matrix_item_estimate_use_groups: AreaMatrixItemEstimateUseGroup[],
  estimate_use_groups: EstimateUseGroup[],
  floor_groups: FloorGroup[]
): FloorAreaItem[] => {
  const validFloors = area_matrix_items.filter((floor) => !floor.deleted_at);

  // Step 2: Group by floor_group, assign label or "default"
  const groupedFloors: Record<string, Record<string, any>[]> = validFloors.reduce(
    (acc, floor) => {
      const floorGroup = floor_groups?.find((fg) => fg.id === floor.floor_group);
      const floorGroupLabel = floorGroup ? floorGroup.label : "unassigned";

      // Initialize group if not present
      if (!acc[floorGroupLabel]) {
        acc[floorGroupLabel] = [];
      }

      acc[floorGroupLabel].push(floor);
      return acc;
    },
    {} as Record<string, Record<string, any>[]>
  );

  // Helper to sort by floor number
  const sortByFloorNumber = (type: string) => (a: AreaMatrixItem, b: AreaMatrixItem) => {
    if (type === "ABOVE_GRADE") return b.floor_number - a.floor_number; // Descending
    return a.floor_number - b.floor_number; // Ascending
  };

  // Step 3: Group floors by floor_type and sort
  const sortedFloors: Record<string, any>[] = [];
  for (const [floorGroupLabel, floors] of Object.entries(groupedFloors)) {
    // Group floors by floor_type
    const floorsByType = {
      ABOVE_GRADE: (floors.filter((floor) => floor.floor_type === "ABOVE_GRADE") as AreaMatrixItem[]).sort(
        sortByFloorNumber("ABOVE_GRADE")
      ),
      BELOW_GRADE: (floors.filter((floor) => floor.floor_type === "BELOW_GRADE") as AreaMatrixItem[]).sort(
        sortByFloorNumber("BELOW_GRADE")
      ),
      FEATURES: (floors.filter((floor) => floor.floor_type === "FEATURE") as AreaMatrixItem[]).sort(
        sortByFloorNumber("FEATURE")
      ),
    };

    // Combine the sorted results and add the floor group label
    Object.values(floorsByType).forEach((groupedFloors) => {
      groupedFloors.forEach((floor) => {
        sortedFloors.push({
          ...floor,
          floor_group_label: floorGroupLabel, // Add floor group label
        });
      });
    });
  }

  const finalFloors: FloorAreaItem[] = sortedFloors.map((floor) => {
    const floorUseGroups = area_matrix_item_estimate_use_groups.filter(
      (amieug) => amieug.area_matrix_item === floor.id
    );

    return {
      type: floor.floor_type,
      label: floor.floor_label,
      floor_group: floor.floor_group_label,
      structure_area: floor.gross_structure,
      perimeter_length: floor.facade_perimeter,
      party_wall_length: floor.facade_party_wall,
      floor_to_floor_height: floor.facade_floor_to_floor,
      // Calculate total_area, but exclude:
      // - Use groups where exclude_area = true
      // - Floors of type "FEATURE"
      total_area:
        floor.floor_type === "FEATURE"
          ? 0 // Features don't count towards total_area
          : floorUseGroups.reduce((total, amieug) => {
              const useGroup = estimate_use_groups.find((eug) => eug.id === amieug.estimate_use_group);
              return useGroup && useGroup.exclude_area ? total : total + amieug.area;
            }, 0),
      // Spread the individual use group areas for the current floor
      ...floorUseGroups.reduce((acc: { [key: string]: any }, amieug) => {
        const useGroup = estimate_use_groups.find((eug) => eug.id === amieug.estimate_use_group);
        if (useGroup) {
          const excludePrefix = useGroup.exclude_area ? "_exclude" : "";
          acc[`${useGroupAreaLabel(useGroup)}${excludePrefix}_area`] = amieug.area;
        }
        return acc;
      }, {}),
      floor_number: floor.floor_number,
      id: floor.id,
    };
  });

  return finalFloors;
};
